// Pagos de granel (tabla granel_pagos) en las rendiciones de repartos.
// Lo usan /listar_repartos (OpenRendiciones) y /repartos/sucursal (ListaRepartosSucursal).
import React from "react";
import { TableRow, TableCell, Typography, Stack, Chip } from "@mui/material";
import PaymentsIcon from "@mui/icons-material/Payments";
import ReceiptLongIcon from "@mui/icons-material/ReceiptLong";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import CreditCardIcon from "@mui/icons-material/CreditCard";
import PaidIcon from "@mui/icons-material/Paid";
import { UrlApi } from "../../../services/apirest";

// Tipo de medio según su nombre: mismo criterio que el backend (granel_pagos.controller tipoMedio),
// para que la sumatoria coincida con el resumen que se guarda en el pedido.
export const tipoMedio = (nombre = "") => {
    if (/efectivo/i.test(nombre)) return "efectivo";
    if (/cheque/i.test(nombre)) return "cheque";
    if (/transferencia|mercado ?pago/i.test(nombre)) return "transferencia";
    if (/tarjeta|d[eé]bito|cr[eé]dito|posnet|point/i.test(nombre)) return "tarjeta";
    return null;
};

// Color e ícono de cada tipo de medio de pago
const ESTILO_MEDIO = {
    efectivo: { color: "#2E7D32", fondo: "#E8F5E9", Icono: PaymentsIcon },
    cheque: { color: "#6A1B9A", fondo: "#F3E5F5", Icono: ReceiptLongIcon },
    transferencia: { color: "#1565C0", fondo: "#E3F2FD", Icono: AccountBalanceIcon },
    tarjeta: { color: "#EF6C00", fondo: "#FFF3E0", Icono: CreditCardIcon },
};
const ESTILO_OTRO = { color: "#616161", fondo: "#F5F5F5", Icono: PaidIcon };

const estiloMedio = (nombre) => ESTILO_MEDIO[tipoMedio(nombre)] || ESTILO_OTRO;

export const formatearMoneda = (valor) =>
    `$${(Number(valor) || 0).toLocaleString("es-AR", { minimumFractionDigits: 2, maximumFractionDigits: 2 })}`;

// Filas de un mismo pedido sin línea divisoria entre sí (la línea queda al final del pedido)
export const SIN_BORDE = { "& td": { borderBottom: "none" } };

// Pagos vigentes de todos los repartos indicados, en una sola llamada. Ante un error devuelve [].
export const fetchPagosRepartos = async (repartos) => {
    const nros = (Array.isArray(repartos) ? repartos : [])
        .map((r) => r.nro_reparto)
        .filter(Boolean)
        .join(",");
    if (!nros) return [];
    try {
        const response = await fetch(UrlApi + `/granel_pagos/xreparto/${encodeURIComponent(nros)}`);
        if (!response.ok) {
            throw new Error(`Error en la solicitud: ${response.statusText}`);
        }
        const data = await response.json();
        return Array.isArray(data) ? data : [];
    } catch (error) {
        console.error("Error obteniendo pagos de los repartos:", error);
        return [];
    }
};

// Pagos de un pedido dentro del reparto
export const pagosDelPedido = (pagos, pedido, nroReparto) =>
    (pagos || []).filter(
        (pago) => pago.id_pedido === pedido.id && String(pago.nro_reparto) === String(nroReparto)
    );

// Sumatoria de la rendición de un reparto.
// - Cobros por medio desde granel_pagos; los de deuda de cta cte (id_gestion_cc) van aparte y no suman a la venta.
// - Pedidos sin pagos en granel_pagos (anteriores al cambio): se usa el resumen del pedido (importe_*).
// - Venta en cta cte = facturado - cobrado (mínimo 0).
export const calcularCobros = ({ pedidos, pagos, facturas, nroReparto }) => {
    const cobros = { efectivo: 0, cheque: 0, transferencia: 0, tarjeta: 0 };
    let deudasCtaCte = 0;

    (pedidos || []).forEach((pedido) => {
        const pagosPedido = pagosDelPedido(pagos, pedido, nroReparto);
        if (pagosPedido.length > 0) {
            pagosPedido.forEach((pago) => {
                const importe = Number(pago.importe) || 0;
                if (pago.id_gestion_cc) {
                    deudasCtaCte += importe;
                    return;
                }
                const tipo = tipoMedio(pago.medio);
                if (tipo) cobros[tipo] += importe;
            });
        } else {
            cobros.efectivo += Number(pedido.importe_efectivo) || 0;
            cobros.cheque += Number(pedido.importe_cheque) || 0;
            cobros.transferencia += Number(pedido.importe_transferencia) || 0;
            cobros.tarjeta += Number(pedido.importe_tarjeta) || 0;
        }
    });

    const totalCobrado = cobros.efectivo + cobros.cheque + cobros.transferencia + cobros.tarjeta;

    const totalFacturado = (pedidos || []).reduce((acc, pedido) => {
        const facturasPedido = (facturas || []).filter((factura) => factura.id_pedido === pedido.id);
        return acc + facturasPedido.reduce((sum, factura) => sum + (Number(factura.importe_total) || 0), 0);
    }, 0);

    const ctaCte = Math.max(totalFacturado - totalCobrado, 0);

    return {
        ...cobros,
        totalCobrado,
        totalFacturado,
        ctaCte,
        totalVenta: ctaCte + totalCobrado,
        deudasCtaCte,
    };
};

// Filas con cada pago individual del pedido (si no tiene pagos no renderiza nada)
export function FilasPagos({ pagos }) {
    return (pagos || []).map((pago, index, lista) => {
        const { color, fondo, Icono } = estiloMedio(pago.medio);
        const esUltimo = index === lista.length - 1;
        return (
            <TableRow key={`pago-${pago.id}`} sx={esUltimo ? undefined : SIN_BORDE}>
                <TableCell colSpan={2} sx={{ py: 0.5, pl: 4 }}>
                    <Stack direction="row" alignItems="center" spacing={1}>
                        <Typography variant="body2" color="text.secondary">↳</Typography>
                        <Chip
                            icon={<Icono sx={{ "&&": { color } }} />}
                            label={pago.medio || "Sin medio"}
                            size="small"
                            sx={{ bgcolor: fondo, color, fontWeight: 600 }}
                        />
                        {pago.id_gestion_cc ? (
                            <Chip
                                label="Deuda cta cte"
                                size="small"
                                variant="outlined"
                                sx={{ borderColor: "#F9A825", color: "#F57F17" }}
                            />
                        ) : null}
                        {pago.nro_recibo ? (
                            <Typography variant="body2" color="text.secondary">
                                Recibo {pago.nro_recibo}
                            </Typography>
                        ) : null}
                    </Stack>
                </TableCell>
                <TableCell align="right" sx={{ py: 0.5, whiteSpace: "nowrap" }}>
                    {formatearMoneda(pago.importe)}
                </TableCell>
            </TableRow>
        );
    });
}

// Filas de la sumatoria (mismo orden que antes, con Tarjeta y cobro de deudas de cta cte)
export function FilasSumatoria({ totales }) {
    const fila = (label, valor, sx) => (
        <TableRow sx={sx}>
            <TableCell align="right" colSpan={2}>{label}</TableCell>
            <TableCell align="right">${valor.toFixed(2)}</TableCell>
        </TableRow>
    );
    return (
        <>
            {fila("Venta en cta cte", totales.ctaCte)}
            {fila("Efectivo", totales.efectivo)}
            {fila("Cheque", totales.cheque)}
            {fila("Transferencia", totales.transferencia)}
            {fila("Tarjeta", totales.tarjeta)}
            {fila("Total Venta", totales.totalVenta, { bgcolor: "#f5f5f5" })}
            {totales.deudasCtaCte > 0 && fila("Cobro deudas cta cte (no suma a la venta)", totales.deudasCtaCte)}
        </>
    );
}
